<template>
  <v-app id="inspire">
    <MainNavBarVue :showMenu="true"></MainNavBarVue>
    <v-main id="main">
      <router-view />
    </v-main>
    <NotificacoesOperacaoHost />
    <ErroSistema ref="errorDialog" />
    <AlertaSistema ref="alertDialog" />
    <UpdateNotesModal />
  </v-app>
</template>

<style>
.theme--light.v-data-table tbody tr:nth-of-type(even) {
  background-color: rgba(0, 0, 0, 0.2);
}

.theme--dark.v-data-table tbody tr:nth-of-type(even) {
  background-color: rgba(0, 0, 0, 0.2);
}

html::-webkit-scrollbar {
  display: none;
}

html {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

#main {
  background-color: v-bind(backgroundColor)
}
</style>

<script>
import MainNavBarVue from "./components/MainNavBar.vue";
import ErroSistema from "./components/Notifications/ErroSistema.vue";
import AlertaSistema from "./components/Notifications/AlertaSistema.vue";
import NotificacoesOperacaoHost from "./components/Notifications/NotificacoesOperacaoHost.vue";
import UpdateNotesModal from "./components/UpdateNotesModal.vue";
import EventBus from "@/utils/eventBus";
import apiController from "@/utils/apiController";
import {
  getPendingSchedulerDownloads,
  removePendingSchedulerDownload,
  hasPendingSchedulerDownloads,
  getSchedulerDownloadTypeConfig,
  downloadSchedulerFile as requestSchedulerDownload,
  POLL_INTERVAL_MS,
} from "@/utils/schedulerDownloadPending";

export default {
  data: () => ({
    backgroundColor: null,
    schedulerDownloadPollTimer: null,
    schedulerDownloadPolling: false,
  }),
  created() {
    this.backgroundColor = this.$vuetify.theme.themes.light.background;
    // Sessão ativa após recarregar a página: as permissões de acesso ficam só em memória
    if (sessionStorage.getItem("api")) {
      this.$store.dispatch("permissoesAcesso/carregar");
    }
  },
  mounted() {
    EventBus.$on('show-error', (payload) => {
      this.$refs.errorDialog.showError(payload.error, payload.type);
    });

    EventBus.$on('show-alert', (payload) => {
      this.$refs.alertDialog.showAlert(
        payload.mensagem,
        payload.tipo,
        payload.duracao != null ? payload.duracao : 5000,
        {
          acaoTexto: payload.acaoTexto,
          onClick: payload.onClick,
          clickMessage: payload.clickMessage,
        }
      );
    });

    // Confirmação de ação bem-sucedida. Os emissores divergem entre `message` e `mensagem`;
    // aceitar os dois evita calar metade das telas.
    EventBus.$on('show-success', (payload) => {
      const texto = payload && (payload.message != null ? payload.message : payload.mensagem);
      if (!texto) return;
      this.$refs.alertDialog.showAlert(
        texto,
        'success',
        payload.duracao != null ? payload.duracao : 5000,
        {
          acaoTexto: payload.acaoTexto,
          onClick: payload.onClick,
          clickMessage: payload.clickMessage,
        }
      );
    });

    EventBus.$on('scheduler-download-enqueued', () => {
      this.ensureSchedulerDownloadPolling();
    });
    // Backward-compatible alias used by system log export
    EventBus.$on('system-log-export-enqueued', () => {
      this.ensureSchedulerDownloadPolling();
    });

    this.ensureSchedulerDownloadPolling();
  },
  beforeDestroy() {
    EventBus.$off('show-error');
    EventBus.$off('show-alert');
    EventBus.$off('show-success');
    EventBus.$off('scheduler-download-enqueued');
    EventBus.$off('system-log-export-enqueued');
    this.stopSchedulerDownloadPolling();
  },

  components: {
    MainNavBarVue,
    ErroSistema,
    AlertaSistema,
    NotificacoesOperacaoHost,
    UpdateNotesModal,
  },

  props: {},
  computed: {},

  methods: {
    ensureSchedulerDownloadPolling() {
      if (this.schedulerDownloadPollTimer) {
        return;
      }
      if (!hasPendingSchedulerDownloads()) {
        return;
      }
      this.schedulerDownloadPollTimer = setInterval(() => {
        this.pollSchedulerDownloads();
      }, POLL_INTERVAL_MS);
      this.pollSchedulerDownloads();
    },

    stopSchedulerDownloadPolling() {
      if (this.schedulerDownloadPollTimer) {
        clearInterval(this.schedulerDownloadPollTimer);
        this.schedulerDownloadPollTimer = null;
      }
    },

    async pollSchedulerDownloads() {
      if (this.schedulerDownloadPolling) {
        return;
      }
      const pending = getPendingSchedulerDownloads();
      if (!pending.length) {
        this.stopSchedulerDownloadPolling();
        return;
      }

      this.schedulerDownloadPolling = true;
      try {
        for (const item of pending) {
          await this.pollOneSchedulerDownload(item);
        }
      } finally {
        this.schedulerDownloadPolling = false;
        if (!hasPendingSchedulerDownloads()) {
          this.stopSchedulerDownloadPolling();
        }
      }
    },

    pollOneSchedulerDownload(item) {
      const typeConfig = getSchedulerDownloadTypeConfig(item.type);
      if (!typeConfig) {
        removePendingSchedulerDownload(item.id_processo);
        return Promise.resolve();
      }

      const messages = typeConfig.messages || {};

      return new Promise((resolve) => {
        apiController.request(apiController.endpoints.schedulerGetProcessoschedulerbyid, {
          data: { id_processo: item.id_processo },
          silent: true,
          onSuccess: (response) => {
            const processo = response.data && response.data.processo;
            const resultado = response.data && response.data.resultado;
            const status = processo && processo.status;

            if (status === 'DONE') {
              removePendingSchedulerDownload(item.id_processo);
              if (typeConfig.updatedEvent) {
                EventBus.$emit(typeConfig.updatedEvent);
              }
              EventBus.$emit('scheduler-download-updated');

              if (resultado && resultado.erro) {
                EventBus.$emit('show-alert', {
                  mensagem: resultado.mensagem || this.$t(messages.failed),
                  tipo: 'error',
                });
              } else {
                const idProcesso = item.id_processo;
                const downloadType = item.type;
                EventBus.$emit('show-alert', {
                  mensagem: this.$t(messages.ready),
                  tipo: 'success',
                  duracao: 0,
                  acaoTexto: this.$t(messages.download),
                  clickMessage: true,
                  onClick: () => this.downloadSchedulerFile(downloadType, idProcesso),
                });
              }
            } else if (status === 'FAILED' || status === 'CANCELED') {
              removePendingSchedulerDownload(item.id_processo);
              if (typeConfig.updatedEvent) {
                EventBus.$emit(typeConfig.updatedEvent);
              }
              EventBus.$emit('scheduler-download-updated');
              EventBus.$emit('show-alert', {
                mensagem:
                  (processo && processo.mensagem_scheduler) ||
                  this.$t(messages.failed),
                tipo: 'error',
              });
            }
            resolve();
          },
          onError: () => resolve(),
        });
      });
    },

    downloadSchedulerFile(type, idProcesso) {
      requestSchedulerDownload(type, idProcesso, (key) => this.$t(key));
    },
  },
};
</script>
